import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';
import { FormData, ConsentState, SingleAnswerSlide, FormInputSlide } from './types';
import { useSliderNavigation } from './hooks';
import ProgressBar from './components/ProgressBar';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import ZipCodeCells from './components/ZipCodeCells';
import ValuationRange from './components/ValuationRange';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../../shared/consent/OptionalConsents';
import { productTypeFromFotovoltaico, submitLead, updateConsent } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';
import { composePhone } from './utils/phone';
import {
  bonificoBand,
  formatEur,
  heatBounds,
  quote,
  shrinkTowardCenter,
  zoneBounds,
  zoneFromCap,
  zoneMax,
} from './config/valuation';

const CHOICE_SLIDES = new Set([
  'boiler_type',
  'install_timeline',
  'heating_system',
  'house_size',
  'fotovoltaico',
  'payout',
]);

const SliderValutazioneCaldaia: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({ phonePrefix: '+39' });
  const [leadUid, setLeadUid] = useState('');
  const submittingRef = useRef(false);
  const [logoOk, setLogoOk] = useState(true);
  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );
  const advanceTimer = useRef<number | null>(null);
  const advancingRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const { currentSlide, currentProgressStep, totalProgressSteps, goNext, goToSlideById } =
    useSliderNavigation(formData);

  const stepIdRef = useRef(currentSlide?.id);
  const goNextRef = useRef(goNext);
  stepIdRef.current = currentSlide?.id;
  goNextRef.current = goNext;

  const zone = zoneFromCap(formValues.zip || formData.zip || '');
  const valuation = zone
    ? quote(zone, formData.heating_system, formData.house_squared_meters)
    : null;
  const bounds = zone ? zoneBounds(zone) : null;

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  useEffect(() => {
    advancingRef.current = false;
    const title = cardRef.current?.querySelector<HTMLElement>('[data-step-title]');
    title?.focus();
  }, [currentSlide?.id]);

  useEffect(() => {
    if (currentSlide?.id !== 'location_form') return;
    const cap = (formValues.zip || '').replace(/\D/g, '').slice(0, 5);
    if (cap.length !== 5 || !zoneFromCap(cap)) return;
    if (advancingRef.current) return;

    advancingRef.current = true;
    const fromId = currentSlide.id;
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => {
      advancingRef.current = false;
      advanceTimer.current = null;
      if (stepIdRef.current !== fromId) return;
      trackSlideAnswer(fromId, currentSlide.title || '', 'continue_button');
      goNextRef.current();
    }, 900);

    return () => {
      if (advanceTimer.current) {
        window.clearTimeout(advanceTimer.current);
        advanceTimer.current = null;
      }
      advancingRef.current = false;
    };
  }, [formValues.zip, currentSlide?.id, currentSlide?.title]);

  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      if (advancingRef.current) return;
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);
      if (!slide.autoAdvance) return;

      advancingRef.current = true;
      const fromId = slide.id;
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
      advanceTimer.current = window.setTimeout(() => {
        advancingRef.current = false;
        advanceTimer.current = null;
        if (stepIdRef.current === fromId) goNextRef.current();
      }, 350);
    },
    [updateFormData]
  );

  const handleFormInputChange = useCallback((name: string, value: string) => {
    const next = name === 'zip' ? value.replace(/\D/g, '').slice(0, 5) : value;
    setFormValues((prev) => ({ ...prev, [name]: next }));
    if (name === 'zip' || name === 'name' || name === 'email' || name === 'phone') {
      updateFormData(name, next);
    }
  }, [updateFormData]);

  const handleLeadSubmit = useCallback(async (consent: ConsentState) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    goToSlideById('loading');
    const uid = uuidv4();
    setLeadUid(uid);
    const phone = composePhone(formValues.phonePrefix ?? '+39', formValues.phone || '');
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
      product_type: productTypeFromFotovoltaico(formData.fotovoltaico),
      phone,
    };

    const valoreContoTermico =
      valuation && valuation.value !== null && valuation.high !== null
        ? formData.payout === 'wire'
          ? bonificoBand(valuation.value, valuation.high).high
          : valuation.high
        : null;

    try {
      const id = await submitLead(finalFormData, uid, consent, valoreContoTermico);
      if (id) {
        trackSliderSuccess();
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          window.setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }
        goToSlideById('success');
      } else {
        submittingRef.current = false;
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      submittingRef.current = false;
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, goToSlideById, valuation]);

  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide, consent?: ConsentState) => {
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });
      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');
      if (slide.id === 'phone_form') {
        await handleLeadSubmit(consent ?? consentForApi);
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext, handleLeadSubmit, consentForApi]
  );

  const handleConsentsUpdate = useCallback(
    (next: ConsentState) => {
      setOptionalConsents({
        enpalMarketingConsent: next.enpalMarketingConsent,
        thirdPartyMarketing: next.thirdPartyMarketing,
        thirdPartyProfiling: next.thirdPartyProfiling,
      });
      if (leadUid) updateConsent(leadUid, next);
    },
    [leadUid]
  );

  const range = (() => {
    if (!bounds || !zone) return null;
    const ready = (min: number, max: number) => ({
      min,
      max,
      baseMin: bounds.min,
      baseMax: bounds.max,
      status: 'ready' as const,
    });

    if (valuation && valuation.value !== null && valuation.high !== null) {
      if (formData.payout === 'wire') {
        const band = bonificoBand(valuation.value, valuation.high);
        return ready(band.low, band.high);
      }
      if (formData.payout === 'trade') {
        return ready(valuation.value, valuation.high);
      }
      const shown = formData.fotovoltaico
        ? shrinkTowardCenter(valuation.value, valuation.high, 1)
        : { min: valuation.value, max: valuation.high };
      return ready(shown.min, shown.max);
    }

    if (formData.heating_system) {
      const heat = heatBounds(zone, formData.heating_system);
      return ready(heat.min, heat.max);
    }

    const openSteps = (formData.boiler_type ? 1 : 0) + (formData.purchase_time ? 1 : 0);
    const shown = shrinkTowardCenter(bounds.min, bounds.max, openSteps);
    return ready(shown.min, shown.max);
  })();

  const successAmount = (() => {
    if (!valuation || valuation.value === null || valuation.high === null) return null;
    if (formData.payout === 'wire') {
      return bonificoBand(valuation.value, valuation.high).high;
    }
    return valuation.high;
  })();

  const renderSlideContent = () => {
    if (!currentSlide) return null;

    if (currentSlide.id === 'location_form') {
      const cap = (formValues.zip || '').replace(/\D/g, '').slice(0, 5);
      const capZone = zoneFromCap(cap);
      const known = cap.length === 5 && !!capZone;
      return (
        <>
          <StepTitle title={currentSlide.title || ''} />
          {known && capZone && (
            <div className={styles.capValue}>
              <span>
                La tua caldaia vale fino a{' '}
                <span className={styles.capAmount}>{formatEur(zoneMax(capZone))}</span>
              </span>
            </div>
          )}
          <ZipCodeCells
            value={cap}
            invalid={cap.length === 5 && !capZone}
            onChange={(next) => handleFormInputChange('zip', next)}
          />
          {cap.length === 5 && !capZone && (
            <p className={styles.capError}>Questo CAP non risulta in elenco.</p>
          )}
        </>
      );
    }

    if (currentSlide.type === 'SINGLE_ANSWER') {
      const slide = currentSlide as SingleAnswerSlide;
      return (
        <>
          <StepTitle title={slide.title || ''} />
          <ChoiceGrid
            answers={slide.answers}
            selectedValue={formData[slide.apiFieldName]}
            onSelect={(value) => handleAnswerSelect(slide, value)}
          />
        </>
      );
    }

    if (currentSlide.type === 'FORM_INPUT') {
      const slide = currentSlide as FormInputSlide;
      const isPhoneSlide = slide.id === 'phone_form';
      return (
        <>
          {slide.title && <StepTitle title={slide.title} />}
          <FormInput
            inputs={slide.inputs}
            values={formValues}
            onChange={handleFormInputChange}
            onSubmit={() => handleFormSubmit(slide)}
            submitLabel="Avanti"
            isPhoneSlide={isPhoneSlide}
            optionalConsents={isPhoneSlide ? optionalConsents : undefined}
            onOptionalConsentsChange={isPhoneSlide ? setOptionalConsents : undefined}
            onPhoneContinue={
              isPhoneSlide
                ? (answers) => {
                    setOptionalConsents(answers);
                    handleFormSubmit(slide, {
                      enpalMarketingConsent: answers.enpalMarketingConsent === true,
                      thirdPartyMarketing: answers.thirdPartyMarketing === true,
                      thirdPartyProfiling: answers.thirdPartyProfiling === true,
                    });
                  }
                : undefined
            }
          />
        </>
      );
    }

    if (currentSlide.id === 'loading') {
      return (
        <>
          <StepTitle title={currentSlide.title || ''} />
          <p className={styles.statusText}>Attendere prego...</p>
        </>
      );
    }

    if (currentSlide.id === 'success') {
      return (
        <SuccessSlide
          consents={consentForApi}
          onConsentsChange={handleConsentsUpdate}
          amountLabel={successAmount && successAmount > 0 ? formatEur(successAmount) : null}
        />
      );
    }

    if (currentSlide.id === 'error') {
      return (
        <>
          <StepTitle title={currentSlide.title || ''} />
          <p className={styles.statusText}>Si è verificato un errore. Riprova tra poco.</p>
          <button type="button" className={styles.introCta} onClick={() => goToSlideById('phone_form')}>
            Riprova
          </button>
        </>
      );
    }

    return null;
  };

  const showProgress = currentSlide?.type === 'SINGLE_ANSWER' || currentSlide?.type === 'FORM_INPUT';
  const showRange = Boolean(currentSlide && CHOICE_SLIDES.has(currentSlide.id) && range);

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        <div className={styles.card} ref={cardRef}>
          {showProgress && (
            <ProgressBar step={currentProgressStep} total={totalProgressSteps} />
          )}
          {showRange && range && (
            <ValuationRange
              min={range.min}
              max={range.max}
              baseMin={range.baseMin}
              baseMax={range.baseMax}
              status={range.status}
            />
          )}
          <div key={currentSlide?.id || 'empty'} className={styles.step}>
            {renderSlideContent()}
          </div>
          <footer className={styles.brandFoot}>
            <span>powered by</span>
            {logoOk ? (
              <img
                className={styles.brandLogo}
                src={`${process.env.PUBLIC_URL}/assets/enpal-logo.svg`}
                alt="Enpal"
                onError={() => setLogoOk(false)}
              />
            ) : (
              <span className={styles.brandName}>Enpal</span>
            )}
          </footer>
        </div>
      </div>
    </section>
  );
};

function StepTitle({ title }: { title: string }) {
  const bare = title.replace(/[?.…!]+$/g, '').trim();
  const mark = /[?？]\s*$/.test(title) || /^(che|come|quando|quanto|entro|hai|quale)\b/i.test(bare)
    ? '?'
    : '.';

  return (
    <h2 className={styles.wizardTitle} tabIndex={-1} data-step-title>
      {bare}
      <span className={styles.punct}>{mark}</span>
    </h2>
  );
}

export default SliderValutazioneCaldaia;
